import MobileMenu, { MobileMenuCSS } from "./MobileMenu";
import { supabase } from "./supabaseClient";
import { useState, useEffect } from "react";

const SERVER = "https://legisim-server.onrender.com";

const TAG_COLORS = {
  POLITICS:    { bg: "rgba(201,168,76,0.1)",  border: "rgba(201,168,76,0.3)",  color: "#c9a84c" },
  ECONOMY:     { bg: "rgba(59,111,212,0.1)",  border: "rgba(59,111,212,0.3)",  color: "#6b9eff" },
  LEGISLATION: { bg: "rgba(155,77,202,0.1)",  border: "rgba(155,77,202,0.3)",  color: "#c084fc" },
  BUDGET:      { bg: "rgba(46,160,67,0.1)",   border: "rgba(46,160,67,0.3)",   color: "#4ade80" },
  ELECTIONS:   { bg: "rgba(239,68,68,0.1)",   border: "rgba(239,68,68,0.3)",   color: "#f87171" },
  JUDICIARY:   { bg: "rgba(251,146,60,0.1)",  border: "rgba(251,146,60,0.3)",  color: "#fb923c" },
  ENVIRONMENT: { bg: "rgba(20,184,166,0.1)",  border: "rgba(20,184,166,0.3)",  color: "#2dd4bf" },
};

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,800;0,900;1,700&family=IBM+Plex+Sans:wght@300;400;500&family=IBM+Plex+Mono:wght@400;500&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
html,body{height:100%;}
/* Theme variables defined globally in public/theme.css */
body{background:var(--bg);color:var(--text);font-family:var(--font-body);-webkit-font-smoothing:antialiased;}
.page{display:flex;flex-direction:column;height:100vh;overflow:hidden;background:var(--bg);}

/* MASTHEAD */
.legisim-masthead{flex-shrink:0;background:var(--surface);border-bottom:2px solid rgba(201,168,76,0.22);padding:0 1.25rem;display:flex;align-items:center;justify-content:space-between;height:58px;position:relative;z-index:20;}
.legisim-masthead::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:linear-gradient(90deg,transparent 0%,var(--gold) 30%,#e8c96a 50%,var(--gold) 70%,transparent 100%);}
.legisim-masthead::after{content:'';position:absolute;inset:0;background-image:linear-gradient(rgba(201,168,76,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(201,168,76,0.025) 1px,transparent 1px);background-size:44px 44px;pointer-events:none;}
.masthead-left{display:flex;align-items:center;gap:1rem;z-index:1;}
.masthead-right{display:flex;align-items:center;gap:0.6rem;z-index:1;}
.mast-back{background:transparent;border:2px solid var(--gold-dim);border-radius:8px;padding:0.45rem 1.1rem;color:var(--gold);font-family:var(--font-mono);font-size:0.72rem;font-weight:600;cursor:pointer;letter-spacing:0.07em;transition:all 0.18s;white-space:nowrap;}
.mast-back:hover{border-color:var(--gold);background:var(--gold-glow);}
.mast-btn-sm{background:transparent;border:1px solid var(--border);border-radius:8px;padding:0.4rem 1rem;color:var(--muted);font-family:var(--font-mono);font-size:0.7rem;cursor:pointer;letter-spacing:0.06em;transition:all 0.18s;white-space:nowrap;}
.mast-btn-sm:hover{border-color:var(--gold-dim);color:var(--gold);}
.mast-btn-sm.signout:hover{border-color:rgba(192,57,43,0.5);color:var(--red);}
.masthead-wordmark{font-family:var(--font-display);font-size:1.35rem;font-weight:800;letter-spacing:-0.02em;color:var(--text);}
.masthead-wordmark em{font-style:italic;color:var(--gold);}
.masthead-wordmark sup{font-family:var(--font-mono);font-size:0.42rem;color:var(--muted);vertical-align:super;}
.masthead-divider{width:1px;height:20px;background:var(--border);}
.masthead-tagline{font-family:var(--font-mono);font-size:0.58rem;color:var(--muted);letter-spacing:0.12em;text-transform:uppercase;}
.masthead-badge{display:inline-flex;align-items:center;gap:0.3rem;background:rgba(201,168,76,0.08);border:1px solid rgba(201,168,76,0.2);border-radius:100px;padding:0.18rem 0.6rem;font-family:var(--font-mono);font-size:0.54rem;color:var(--gold);letter-spacing:0.1em;text-transform:uppercase;}
.masthead-badge-dot{width:5px;height:5px;border-radius:50%;background:var(--gold);animation:mbpulse 2.5s infinite;}
@keyframes mbpulse{0%,100%{opacity:0.4;transform:scale(0.85)}50%{opacity:1;transform:scale(1.1)}}
.masthead-nav{display:flex;align-items:center;gap:0.3rem;z-index:1;flex-shrink:1;overflow:hidden;}
.mast-btn{background:transparent;border:1px solid var(--border);border-radius:6px;padding:0.36rem 0.7rem;color:var(--muted);font-family:var(--font-mono);font-size:0.64rem;cursor:pointer;letter-spacing:0.07em;transition:all 0.18s;white-space:nowrap;}
.mast-btn:hover{border-color:var(--gold-dim);color:var(--gold);background:rgba(201,168,76,0.05);}
.mast-btn.gold{border-color:var(--gold-dim);color:var(--gold);}
.mast-btn.gold:hover{background:rgba(201,168,76,0.1);}
.mast-btn.active{border-color:var(--gold);color:var(--gold);background:rgba(201,168,76,0.1);}
.mast-sep{width:1px;height:16px;background:var(--border);margin:0 0.1rem;}

/* BODY */
.news-body{flex:1;overflow:hidden;display:flex;flex-direction:column;}

/* TOP BAR */
.news-topbar{flex-shrink:0;padding:0.75rem 2rem;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;background:var(--surface);}
.news-topbar-title{font-family:var(--font-display);font-size:1rem;font-weight:800;color:var(--text);letter-spacing:-0.01em;}
.news-topbar-title em{font-style:italic;color:var(--gold);}
.news-topbar-meta{font-family:var(--font-mono);font-size:0.6rem;color:var(--muted);}

/* COLUMNS — desktop two-column layout */
.news-columns{flex:1;display:grid;grid-template-columns:1fr 1fr;overflow:hidden;border-top:1px solid var(--border);}
.news-col{display:flex;flex-direction:column;overflow:hidden;}
.news-col:first-child{border-right:1px solid var(--border);}
/* Tab toggle — mobile only, hidden on desktop */
.news-tabs{display:none;}
.news-single-col{display:none;}

/* COLUMN HEADER */
.col-header{flex-shrink:0;padding:1.25rem 2rem 1rem;border-bottom:1px solid var(--border);}
.col-header-row{display:flex;align-items:baseline;gap:0.75rem;}
.col-title{font-family:var(--font-display);font-size:1.5rem;font-weight:900;letter-spacing:-0.02em;}
.col-title.michigan{color:var(--gold);}
.col-title.national{color:#6b9eff;}
.col-subtitle{font-family:var(--font-mono);font-size:0.6rem;color:var(--muted);letter-spacing:0.1em;text-transform:uppercase;}

/* STORY LIST */
.story-list{flex:1;overflow-y:auto;scrollbar-width:thin;scrollbar-color:rgba(255,255,255,0.05) transparent;}

/* STORY */
.story{padding:1.1rem 2rem;border-bottom:1px solid rgba(255,255,255,0.045);transition:background 0.15s;}
.story:hover{background:rgba(255,255,255,0.02);}
.story:last-child{border-bottom:none;}
.story-link{font-size:1rem;font-weight:500;color:var(--text);text-decoration:none;line-height:1.5;display:block;margin-bottom:0.35rem;transition:color 0.15s;}
.story:hover .story-link{color:var(--gold);}
.news-col:last-child .story:hover .story-link{color:#6b9eff;}
.story-excerpt{font-size:0.78rem;color:var(--muted);line-height:1.5;margin-bottom:0.5rem;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;}
.story-footer{display:flex;align-items:center;gap:0.5rem;flex-wrap:wrap;}
.story-tags{display:flex;gap:0.3rem;flex-wrap:wrap;}
.story-tag{font-family:var(--font-mono);font-size:0.5rem;font-weight:600;letter-spacing:0.07em;padding:0.12rem 0.4rem;border-radius:3px;border:1px solid;text-transform:uppercase;}
.story-byline{display:flex;align-items:center;gap:0.35rem;margin-left:auto;flex-shrink:0;}
.story-source{font-family:var(--font-mono);font-size:0.6rem;color:var(--muted);}
.story-sep{width:2px;height:2px;border-radius:50%;background:var(--muted);opacity:0.5;}
.story-time{font-family:var(--font-mono);font-size:0.58rem;color:var(--muted);opacity:0.6;}

/* LOADING */
.news-loader{display:flex;flex-direction:column;align-items:center;justify-content:center;flex:1;gap:1rem;}
.loader-dots{display:flex;gap:0.4rem;}
.loader-dot{width:7px;height:7px;border-radius:50%;background:var(--gold);animation:ldot 1.3s infinite;}
.loader-dot:nth-child(2){animation-delay:0.15s;}
.loader-dot:nth-child(3){animation-delay:0.3s;}
@keyframes ldot{0%,80%,100%{opacity:0.15;transform:scale(0.7)}40%{opacity:1;transform:scale(1)}}
.loader-text{font-family:var(--font-mono);font-size:0.65rem;color:var(--muted);letter-spacing:0.1em;}
.news-error{display:flex;align-items:center;justify-content:center;flex:1;font-family:var(--font-mono);font-size:0.72rem;color:var(--muted);}
@media(max-width:768px){
  .masthead-tagline{display:none;}
  .masthead-badge{display:none;}
  .masthead-nav{display:none !important;}
  .legisim-masthead{flex-shrink:0;background:var(--surface);border-bottom:2px solid rgba(201,168,76,0.22);padding:0 1.25rem;display:flex;align-items:center;justify-content:space-between;height:58px;position:relative;z-index:20;}
  .news-columns{display:none;}
  .news-tabs{display:flex;flex-shrink:0;border-bottom:1px solid var(--border);background:var(--surface);}
  .news-tab{flex:1;padding:0.75rem 1rem;font-family:var(--font-display);font-size:1.15rem;font-weight:800;letter-spacing:-0.01em;cursor:pointer;background:transparent;border:none;color:var(--muted);transition:all 0.18s;position:relative;}
  .news-tab::after{content:'';position:absolute;bottom:0;left:0;right:0;height:2px;background:transparent;transition:background 0.18s;}
  .news-tab:hover{color:var(--text);}
  .news-tab.active.michigan{color:var(--gold);}
  .news-tab.active.michigan::after{background:var(--gold);}
  .news-tab.active.national{color:#6b9eff;}
  .news-tab.active.national::after{background:#6b9eff;}
  .news-single-col{display:flex;flex:1;overflow:hidden;flex-direction:column;}
  .news-topbar{padding:0.6rem 1rem;}
  .story{padding:0.85rem 1rem;}
}
  .story{padding:0.85rem 1rem;}
  .col-header{padding:0.85rem 1rem 0.75rem;}
}

@media(max-width:1200px){
  .mast-btn{padding:0.3rem 0.45rem;font-size:0.58rem;letter-spacing:0.03em;}
  .masthead-nav{gap:0.2rem;}
}
@media(max-width:900px){
  .mast-btn{padding:0.28rem 0.38rem;font-size:0.55rem;}
  .masthead-nav{gap:0.15rem;}
}
@media(max-width:768px){
  .masthead-nav{display:none !important;}
  .legisim-masthead{flex-shrink:0;background:var(--surface);border-bottom:2px solid rgba(201,168,76,0.22);padding:0 1.25rem;display:flex;align-items:center;justify-content:space-between;height:58px;position:relative;z-index:20;}
}

/* ── LIGHT MODE OVERRIDES ── */
[data-theme="light"] body{background:#eef1f7;color:#1a1e2e;}
[data-theme="light"] .page,[data-theme="light"] .app,[data-theme="light"] .bt-app,[data-theme="light"] .tl-app,[data-theme="light"] .map-app,[data-theme="light"] .cf-app{background:#eef1f7;}
[data-theme="light"] .legisim-masthead{background:#1a2744;border-bottom-color:rgba(255,255,255,0.1);}
[data-theme="light"] .legisim-masthead::before{background:linear-gradient(90deg,transparent,var(--gold),transparent);}
[data-theme="light"] .masthead-wordmark{color:#e6e4dc;}
[data-theme="light"] .masthead-wordmark em{color:#c9a84c;}
[data-theme="light"] .masthead-wordmark sup{color:rgba(230,228,220,0.5);}
[data-theme="light"] .mast-back{border-color:rgba(201,168,76,0.6);color:#c9a84c;background:transparent;}
[data-theme="light"] .mast-back:hover{background:rgba(201,168,76,0.12);border-color:#c9a84c;color:#c9a84c;}
[data-theme="light"] .mast-btn-sm{color:#e6e4dc;border-color:rgba(201,168,76,0.4);}
[data-theme="light"] .mast-btn-sm:hover{color:#ffffff;border-color:#c9a84c;background:rgba(201,168,76,0.1);}
[data-theme="light"] .mast-btn-sm.signout:hover{border-color:rgba(192,57,43,0.7);color:#ff6b6b;background:rgba(192,57,43,0.1);}
[data-theme="light"] .sidebar,[data-theme="light"] .bt-left,[data-theme="light"] .cal-sidebar,[data-theme="light"] .cf-sidebar,[data-theme="light"] .map-left{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .main,[data-theme="light"] .bt-detail,[data-theme="light"] .cf-main{background:#eef1f7;}
[data-theme="light"] .filter-btn,[data-theme="light"] .vh-filter-btn{background:#f8f9fc;border-color:rgba(26,39,68,0.12);color:#6b7280;}
[data-theme="light"] .filter-btn.active,[data-theme="light"] .vh-filter-btn.active{background:rgba(26,39,68,0.1);border-color:rgba(26,39,68,0.3);color:#1a2744;}
[data-theme="light"] .filter-btn.dem-active{background:var(--dem-bg);border-color:var(--dem);color:var(--dem);}
[data-theme="light"] .filter-btn.rep-active{background:var(--rep-bg);border-color:var(--rep);color:var(--rep);}
[data-theme="light"] .search-input,[data-theme="light"] .chat-input,[data-theme="light"] .bt-search-input,[data-theme="light"] .tl-search-input{background:#f8f9fc;border-color:rgba(26,39,68,0.12);color:#1a1e2e;}
[data-theme="light"] .leg-item.selected{background:#e8ecf4;}
[data-theme="light"] .leg-item.selected::before{background:#1a2744;}
[data-theme="light"] .leg-item:hover{background:#e8ecf4;}
[data-theme="light"] .chat-input-area{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .msg-bubble{background:#f0f2f8;border-color:rgba(26,39,68,0.1);}
[data-theme="light"] .msg.user .msg-bubble{background:#e8ecf4;border-color:rgba(26,39,68,0.2);}
[data-theme="light"] .send-btn{background:#1a2744;color:#f8f9fc;}
[data-theme="light"] .main-topbar,[data-theme="light"] .news-topbar,[data-theme="light"] .chamber-bar,[data-theme="light"] .toggle-bar{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .vh-header,[data-theme="light"] .vh-filters,[data-theme="light"] .vh-stats{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .vh-bill-number,[data-theme="light"] .bt-bill-number,[data-theme="light"] .bt-detail-number,[data-theme="light"] .tl-bill-number,[data-theme="light"] .district-num{color:#1a2744;}
[data-theme="light"] .profile-section-label,[data-theme="light"] .section-label,[data-theme="light"] .form-label,[data-theme="light"] .panel-title,[data-theme="light"] .output-label,[data-theme="light"] .floor-title,[data-theme="light"] .detail-title,[data-theme="light"] .search-section-label{color:#1a2744;}
[data-theme="light"] .input-panel{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .output-panel,[data-theme="light"] .output-body{background:#eef1f7;}
[data-theme="light"] .tl-add-btn,[data-theme="light"] .tl-refresh-btn,[data-theme="light"] .tl-view-btn{color:#1a2744;border-color:rgba(26,39,68,0.2);}
[data-theme="light"] .tl-table-head{background:#e8ecf4;}
[data-theme="light"] .tl-tr:hover{background:#e8ecf4;}
[data-theme="light"] .date-group-header{color:#1a2744;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .detail-header,[data-theme="light"] .profile-header,[data-theme="light"] .donors-section,[data-theme="light"] .bt-detail-header{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .acct-card{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .news-tab.active{color:#1a2744;border-color:#1a2744;}
[data-theme="light"] .story-source{color:#6b7280;}
[data-theme="light"] .profile-sidebar{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .home-title{color:#1a2744;}
[data-theme="light"] .cf-amount,[data-theme="light"] .cf-total{color:#1a2744;}
[data-theme="light"] .chamber-btn.active{background:rgba(26,39,68,0.1);color:#1a2744;border-color:rgba(26,39,68,0.3);}
[data-theme="light"] .swing-chip{background:rgba(26,39,68,0.1);color:#1a2744;}
[data-theme="light"] .score-cell.pass{background:rgba(37,107,66,0.12);}
[data-theme="light"] .score-cell.fail{background:rgba(176,48,32,0.08);}
[data-theme="light"] .map-tooltip-district{color:#1a2744;}
[data-theme="light"] .watchlist-btn-small,[data-theme="light"] .watch-btn{border-color:rgba(160,120,48,0.4);color:#a07830;}
[data-theme="light"] .vote-history-btn{background:rgba(160,120,48,0.08);border-color:rgba(160,120,48,0.35);color:#a07830;}
[data-theme="light"] .masthead-badge{background:rgba(26,39,68,0.08);border-color:rgba(26,39,68,0.2);}
[data-theme="light"] .masthead-badge-dot{background:#1a2744;}
[data-theme="light"] .mast-btn.gold{border-color:rgba(160,120,48,0.4);color:#a07830;}
[data-theme="light"] .mast-btn.active{background:rgba(26,39,68,0.1);border-color:rgba(26,39,68,0.3);color:#1a2744;}
[data-theme="light"] .issue-tag{background:#e8ecf4;border-color:rgba(26,39,68,0.12);color:#1a1e2e;}
[data-theme="light"] .qp-btn{background:#f0f2f8;border-color:rgba(26,39,68,0.12);color:#6b7280;}
[data-theme="light"] .empty-card{background:#f0f2f8;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .donor-bar-fill{background:linear-gradient(90deg,#1a2744,#2d4a80);}
[data-theme="light"] .cf-leg-item.active,[data-theme="light"] .bt-bill-item.selected{border-left-color:#1a2744;}
[data-theme="light"] .subject-tag{background:#e8ecf4;border-color:rgba(26,39,68,0.12);color:#1a1e2e;}
[data-theme="light"] .plan-badge,[data-theme="light"] .admin{color:#1a2744;}
[data-theme="light"] .login-card{background:#f8f9fc;border-color:rgba(26,39,68,0.12);box-shadow:0 4px 24px rgba(26,39,68,0.1);}
`;

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return "";
  const diff = Math.floor((new Date() - d) / 60000);
  if (diff < 60) return `${diff}m ago`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function Story({ s }) {
  return (
    <div className="story">
      <a className="story-link" href={s.link} target="_blank" rel="noreferrer">{s.title}</a>
      {s.description && <p className="story-excerpt">{s.description}</p>}
      <div className="story-footer">
        <div className="story-tags">
          {s.tags.map(tag => {
            const c = TAG_COLORS[tag] || TAG_COLORS.POLITICS;
            return <span key={tag} className="story-tag" style={{ background: c.bg, borderColor: c.border, color: c.color }}>{tag}</span>;
          })}
        </div>
        <div className="story-byline">
          <span className="story-source">{s.source}</span>
          {s.pubDate && <><div className="story-sep" /><span className="story-time">{formatDate(s.pubDate)}</span></>}
        </div>
      </div>
    </div>
  );
}


async function handleSignOut(supabaseClient, onNavigate) {
  await supabaseClient.auth.signOut();
  onNavigate('home');
}

export default function NewsPage({ onNavigate }) {
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("michigan");

  useEffect(() => {
    fetch(`${SERVER}/api/news`)
      .then(r => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .then(data => { setNews(data); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  const stories = activeTab === "michigan" ? (news?.state || []) : (news?.national || []);

  return (
    <>
      <style>{CSS}</style>
      <MobileMenuCSS />
      <div className="page">
        <div className="legisim-masthead">
          <div className="masthead-left">
            <div className="masthead-wordmark">Whip<em>Count</em><sup>TM</sup></div>
            <button className="mast-back" onClick={() => onNavigate("dashboard")}>← Dashboard</button>
          </div>
          <div className="masthead-right">
            <button className="mast-btn-sm" onClick={() => onNavigate("account")}>Account</button>
            <button className="mast-btn-sm signout" onClick={() => handleSignOut(supabase, onNavigate)}>Sign Out</button>
          </div>
        </div>
        <div className="news-body">
          <div className="news-topbar">
            <div className="news-topbar-title">Legislative Intelligence <em>Briefing</em></div>
            {news?.fetched && <div className="news-topbar-meta">Updated {new Date(news.fetched).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>}
          </div>

          {/* Tab toggle */}

          {loading ? (
            <div className="news-loader">
              <div className="loader-dots">
                <div className="loader-dot" /><div className="loader-dot" /><div className="loader-dot" />
              </div>
              <div className="loader-text">Fetching latest stories...</div>
            </div>
          ) : error ? (
            <div className="news-error">Failed to load news: {error}</div>
          ) : (
            <>
              {/* Desktop: two columns */}
              <div className="news-columns">
                <div className="news-col">
                  <div className="col-header">
                    <div className="col-header-row">
                      <div className="col-title michigan">Michigan</div>
                      <div className="col-subtitle">Top 20</div>
                    </div>
                  </div>
                  <div className="story-list">
                    {(news?.state || []).map((s, i) => <Story key={i} s={s} />)}
                  </div>
                </div>
                <div className="news-col">
                  <div className="col-header">
                    <div className="col-header-row">
                      <div className="col-title national">National</div>
                      <div className="col-subtitle">Top 20</div>
                    </div>
                  </div>
                  <div className="story-list">
                    {(news?.national || []).map((s, i) => <Story key={i} s={s} />)}
                  </div>
                </div>
              </div>

              {/* Mobile: tab toggle + single column */}
              <div className="news-tabs">
                <button className={`news-tab${activeTab === "michigan" ? " active michigan" : ""}`} onClick={() => setActiveTab("michigan")}>Michigan</button>
                <button className={`news-tab${activeTab === "national" ? " active national" : ""}`} onClick={() => setActiveTab("national")}>National</button>
              </div>
              <div className="news-single-col">
                <div className="story-list">
                  {stories.map((s, i) => <Story key={i} s={s} />)}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
